import { useCallback, useRef, useSyncExternalStore } from 'react';
import type { WorkspaceStore } from '../../domain/store';
import type { Workspace } from '../../types';

/**
 * Bridges `WorkspaceStore` (a mutable object mutated in place, see
 * `store.ts`'s `update()`) into React. `store.workspace` keeps the same
 * object reference across an `update()`, so returning it directly as the
 * `useSyncExternalStore` snapshot would make every re-render a no-op
 * (`Object.is` sees no change) — this wraps `subscribe` with a version
 * counter instead, so the snapshot itself changes on every notification.
 * By the time subscribers run, `update()` has already finished mutating
 * `#workspace` synchronously, so reading `store.workspace` fresh here is safe,
 * never stale.
 */
export function useWorkspace(store: WorkspaceStore): Workspace {
  const version = useRef(0);
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      store.subscribe(() => {
        version.current++;
        onStoreChange();
      }),
    [store],
  );
  const getSnapshot = useCallback(() => version.current, []);
  useSyncExternalStore(subscribe, getSnapshot);
  return store.workspace;
}
